import { useState, useRef, useEffect } from 'react';
import { TemplateMessage, TemplateType, ScenarioCharacter } from '../../api/types/scenario';
import { useDevice } from '../../context/DeviceContext';
import { ScenarioEditFieldButton, ScenarioDeleteButton } from './ScenarioCommonButtons';
import styles from './EditableTemplateMessage.module.css';
import ReactDOM from 'react-dom';

const MAX_CONTENT_LENGTH = 2000;

interface EditableTemplateMessageProps {
  message: TemplateMessage;
  characters: ScenarioCharacter[];
  onEdit: (messageId: string, updates: {
    templateType?: TemplateType;
    characterId?: string;
    content?: string;
  }) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
}

function EditableTemplateMessage({ message, characters, onEdit, onDelete }: EditableTemplateMessageProps): JSX.Element {
  const { isMobile } = useDevice();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTemplateType, setEditedTemplateType] = useState<TemplateType>(message.template_type);
  const [editedCharacterId, setEditedCharacterId] = useState<string | undefined>(message.character?.id);
  const [editedContent, setEditedContent] = useState(message.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // When template type changes, reset character ID if needed
  useEffect(() => {
    if (editedTemplateType !== 'character') {
      setEditedCharacterId(undefined);
    } else if (!editedCharacterId && characters.length > 0) {
      setEditedCharacterId(characters[0].id);
    }
  }, [editedTemplateType, editedCharacterId, characters]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
      textareaRef.current.selectionEnd = textareaRef.current.value.length;
      adjustTextareaHeight();
    }
  }, [isEditing]);

  // Prevent body scrolling when mobile overlay is active
  useEffect(() => {
    if (isMobile && isEditing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isEditing]);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };
  
  const handleSave = async () => {
    if (editedContent.length > MAX_CONTENT_LENGTH) return;
    
    try {
      await onEdit(message.id, {
        templateType: editedTemplateType,
        characterId: editedTemplateType === 'character' ? editedCharacterId : undefined,
        content: editedContent,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save message:', error);
    }
  };

  const handleCancel = () => {
    setEditedTemplateType(message.template_type);
    setEditedCharacterId(message.character?.id);
    setEditedContent(message.content);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      await onDelete(message.id);
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_CONTENT_LENGTH) {
      setEditedContent(newValue);
      adjustTextareaHeight();
    }
  };

  const getCharCountClass = () => {
    const ratio = editedContent.length / MAX_CONTENT_LENGTH;
    if (ratio >= 1) return styles.charCountError;
    if (ratio >= 0.9) return styles.charCountWarning;
    return '';
  };

  const renderEditContent = () => (
    <div className={styles.editContainer}>
      <textarea
        ref={textareaRef}
        value={editedContent}
        onChange={handleContentChange}
        onKeyDown={handleKeyDown}
        className={styles.contentTextarea}
        rows={1}
      />
      <div className={styles.editActions}>
        <div className={styles.editHints}>
          <span>Esc to cancel</span>
          <span>Ctrl+Enter to save</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className={`${styles.charCount} ${getCharCountClass()}`}>
            {editedContent.length}/{MAX_CONTENT_LENGTH}
          </span>
          <div className={styles.editButtons}>
            <button 
              onClick={handleCancel}
              className={`${styles.editButton} ${styles.cancelButton}`}
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className={`${styles.editButton} ${styles.saveButton}`}
              disabled={editedContent.length > MAX_CONTENT_LENGTH}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMobileOverlay = () => {
    if (isMobile && isEditing) {
      return ReactDOM.createPortal(
        <div className={styles.mobileEditOverlay}>
          <div className={styles.mobileEditHeader}>
            <button 
              onClick={handleCancel}
              className={styles.mobileBackButton}
              aria-label="Back"
            >
              ‹
            </button>
            <div className={styles.messageTypeSection}>
              <select 
                value={editedTemplateType}
                onChange={(e) => setEditedTemplateType(e.target.value as TemplateType)}
                className={styles.templateTypeSelect}
              >
                <option value="narrator">Narrator</option>
                <option value="character">Character</option>
                <option value="user-slot">{'{{user}}'}</option>
                <option value="char-slot">{'{{char}}'}</option>
              </select>
              
              {editedTemplateType === 'character' && (
                <select
                  value={editedCharacterId}
                  onChange={(e) => setEditedCharacterId(e.target.value)}
                  className={styles.characterSelect}
                >
                  <option value="">Select Character</option>
                  {characters.map((char) => (
                    <option key={char.id} value={char.id}>
                      {char.display_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {renderEditContent()}
        </div>,
        document.body
      );
    }
    return null;
  };

  // Render the appropriate display for the message type
  const renderTemplateTypeDisplay = () => {
    switch(message.template_type) {
      case 'narrator':
        return (
          <span className={`${styles.templatePill} ${styles.narratorPill}`}>
            Narrator
          </span>
        );
      case 'user-slot':
        return (
          <span className={`${styles.templatePill} ${styles.userSlotPill}`}>
            {'{{user}}'}
          </span>
        );
      case 'char-slot':
        return (
          <span className={`${styles.templatePill} ${styles.charSlotPill}`}>
            {'{{char}}'}
          </span>
        );
      case 'character':
        // Character type doesn't show a pill, just the character avatar and name
        return null;
      default:
        return (
          <span className={styles.templatePill}>
            {message.template_type}
          </span>
        );
    }
  };

  const isCharacter = message.template_type === 'character';
  const currentCharacter = message.character;
  
  return (
    <>
      <div className={styles.messageContainer}>
        <div className={styles.messageHeader}>
          <div className={styles.messageControls}>
            <div className={styles.messageTypeSection}>
              {isEditing && !isMobile ? (
                <>
                  <select 
                    value={editedTemplateType}
                    onChange={(e) => setEditedTemplateType(e.target.value as TemplateType)}
                    className={styles.templateTypeSelect}
                  >
                    <option value="narrator">Narrator</option>
                    <option value="character">Character</option>
                    <option value="user-slot">{'{{user}}'}</option>
                    <option value="char-slot">{'{{char}}'}</option>
                  </select>
                  
                  {editedTemplateType === 'character' && (
                    <select
                      value={editedCharacterId}
                      onChange={(e) => setEditedCharacterId(e.target.value)}
                      className={styles.characterSelect}
                    >
                      <option value="">Select Character</option>
                      {characters.map((char) => (
                        <option key={char.id} value={char.id}>
                          {char.display_name}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              ) : (
                renderTemplateTypeDisplay()
              )}
            </div>
            
            {isCharacter && currentCharacter && (
              <>
                <div className={styles.avatarContainer}>
                  <img 
                    src={currentCharacter.icon}
                    alt={currentCharacter.display_name}
                    className={styles.avatar}
                  />
                </div>
                <span className={styles.characterName}>
                  {currentCharacter.display_name}
                </span>
              </>
            )}
          </div>

          {!isEditing && (
            <div className={styles.actions}>
              <ScenarioEditFieldButton
                onClick={() => setIsEditing(true)}
                showPill={false}
                label="Edit message"
                className={styles.editButton}
              />
              <ScenarioDeleteButton
                onClick={handleDelete}
                showPill={false}
                label="Delete message"
                className={styles.deleteButton}
              />
            </div>
          )}
        </div>

        <div className={styles.messageContent}>
          {isEditing && !isMobile ? renderEditContent() : message.content}
        </div>
      </div>
      {renderMobileOverlay()}
    </>
  );
}

export default EditableTemplateMessage; 